import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useTimesStore } from './times.js'
import { useCatalogStore } from './catalog.js'
import { useTimeEntries } from '../composables/useTimeEntries.js'
import { secondsToRoundedTime, timeToMinutes, minutesToTime } from '../utils/timeHelpers.js'
import logger from '../utils/logger.js'

export const STORAGE_KEY = 'srk-timesheet-carga-diaria-timer'

/**
 * Timer global de Carga Diaria - vive acá (Pinia, singleton de toda la app)
 * en vez de adentro de EntryBar.vue, para que sobreviva la navegación entre
 * pantallas (antes, cambiar de ruta desmontaba EntryBar y con eso se perdía
 * el cronómetro corriendo - ver GlobalTimerWidget.vue, que es lo que ahora
 * se muestra en CUALQUIER pantalla mientras isRunning sea true, y
 * CargaView.vue, donde el diálogo de "cronómetro corriendo" al navegar ya no
 * hace falta para no perder el tiempo - solo para decidir qué hacer con él).
 *
 * El guardado (`stopAndSave`) pega directo contra el backend (vía
 * useTimeEntries().saveBatch), sin pasar por el borrador de ninguna pantalla
 * (usePeriodDraft) - necesario porque Stop puede dispararse habiendo
 * navegado lejos de /carga, donde ese borrador ni existe ya. Si DayLoad.vue
 * sigue montado viendo el mismo día y sin cambios propios sin guardar, se
 * entera solo (usePeriodDraft ya re-sincroniza con el cache de timesStore).
 */
export const useActiveTimerStore = defineStore('activeTimer', () => {
  const isRunning = ref(false)
  const elapsedSeconds = ref(0)
  const fecha = ref(null)
  const proyecto = ref(null)
  const tareaId = ref(null)
  const tareaNombre = ref('')
  const desc = ref('')
  const property1 = ref(0)
  const property2 = ref(0)
  // true cuando el Play actual vino de "reanudar" una fila ya guardada - solo
  // afecta el texto del resultado ("Sumado" vs "Guardado"), stopAndSave
  // siempre intenta mergear con una fila existente de todos modos.
  const reanudando = ref(false)

  let startedAt = null
  let baseSeconds = 0
  let intervalId = null

  function tick() {
    elapsedSeconds.value = baseSeconds + Math.floor((Date.now() - startedAt) / 1000)
  }

  function guardarPersistencia() {
    if (!proyecto.value) return
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      startedAtWallClock: Date.now(),
      fecha: fecha.value,
      projectId: proyecto.value.prID,
      tipo: proyecto.value.tipo,
      projectName: proyecto.value.text,
      tareaId: tareaId.value,
      tareaName: tareaNombre.value,
      desc: desc.value,
      property1: property1.value,
      property2: property2.value
    }))
  }

  function limpiarPersistencia() {
    localStorage.removeItem(STORAGE_KEY)
  }

  function resetAll() {
    proyecto.value = null
    tareaId.value = null
    tareaNombre.value = ''
    desc.value = ''
    property1.value = 0
    property2.value = 0
    reanudando.value = false
    fecha.value = null
    elapsedSeconds.value = 0
    baseSeconds = 0
    startedAt = null
  }

  function iniciarIntervalo() {
    clearInterval(intervalId)
    intervalId = setInterval(tick, 1000)
  }

  /** Arranca el timer para una tarea elegida a mano (Play) o al reanudar. */
  function start({ fecha: f, proyecto: p, tareaId: tId, tareaNombre: tN, desc: d, property1: p1, property2: p2, reanudando: r = false }) {
    if (isRunning.value) return
    fecha.value = f
    proyecto.value = p
    tareaId.value = tId
    tareaNombre.value = tN
    desc.value = d
    property1.value = p1
    property2.value = p2
    reanudando.value = r
    baseSeconds = 0
    elapsedSeconds.value = 0
    startedAt = Date.now()
    isRunning.value = true
    iniciarIntervalo()
    guardarPersistencia()
  }

  function stopRaw() {
    if (!isRunning.value) return elapsedSeconds.value
    tick()
    clearInterval(intervalId)
    intervalId = null
    isRunning.value = false
    return elapsedSeconds.value
  }

  /** Corre el ancla de arranque hacia adelante para "restar" un lapso (compu suspendida, etc). */
  function descontar(segundos) {
    if (startedAt == null || segundos <= 0) return
    startedAt += segundos * 1000
    tick()
  }

  /** Para sin guardar nada - usado por "Descartar" (dialog de cronómetro corriendo). */
  function discard() {
    stopRaw()
    limpiarPersistencia()
    resetAll()
  }

  /**
   * Para y guarda directo contra el backend. Devuelve { ok, corto?, error?,
   * tiempoCargado?, proyectoNombre?, sumado? } para que quien llamó muestre
   * el feedback (Swal / resultado en la PiP).
   */
  // Si el guardado falla (sin red, sesión vencida, etc.) el timer se vuelve
  // a poner en marcha con los mismos segundos acumulados, en vez de perderse
  // - para que un Stop fallido nunca tire datos, solo haga que haya que
  // reintentarlo (ej. después de volver a loguearse).
  function reanudarTrasFalloDeGuardado(segundosAcumulados) {
    baseSeconds = segundosAcumulados
    elapsedSeconds.value = segundosAcumulados
    startedAt = Date.now()
    isRunning.value = true
    iniciarIntervalo()
    guardarPersistencia()
  }

  async function stopAndSave() {
    const seg = stopRaw()

    if (seg < 30) {
      limpiarPersistencia()
      resetAll()
      return { ok: false, corto: true }
    }

    const fechaActual = fecha.value
    const proyectoActual = proyecto.value
    const tareaIdActual = tareaId.value
    const descActual = desc.value
    const property1Actual = property1.value
    const property2Actual = property2.value
    const eraReanudacion = reanudando.value
    const tiempoCargado = secondsToRoundedTime(seg)

    const timesStore = useTimesStore()
    const catalogStore = useCatalogStore()
    const { saveBatch } = useTimeEntries()

    let entrada
    let huboMatch = false
    try {
      await timesStore.ensureRange(fechaActual, fechaActual)
      const existentes = timesStore.getDayData(fechaActual, 0)
      const match = existentes.find((e) =>
        e.ts_ProjectId === proyectoActual.prID &&
        e.ts_Tipo === proyectoActual.tipo &&
        catalogStore.encodeTareaId(e) === tareaIdActual &&
        (e.tareadesc || '') === (descActual || '')
      )

      if (match) {
        huboMatch = true
        const minutosExistentes = timeToMinutes(match.tiempo)
        const minutosNuevosCrudos = seg / 60
        const totalRedondeado = Math.round((minutosExistentes + minutosNuevosCrudos) / 5) * 5
        entrada = {
          ts_Id: match.ts_Id,
          ts_ProjectId: proyectoActual.prID,
          ts_Tipo: proyectoActual.tipo,
          tareaId: tareaIdActual,
          tareadesc: descActual,
          tiempo: minutesToTime(totalRedondeado),
          property1: property1Actual,
          property2: property2Actual,
          _new: false,
          _dirty: true,
          _deleted: false
        }
      } else {
        entrada = {
          ts_Id: null,
          ts_ProjectId: proyectoActual.prID,
          ts_Tipo: proyectoActual.tipo,
          ts_Fecha: fechaActual,
          tareaId: tareaIdActual,
          tareadesc: descActual,
          tiempo: tiempoCargado,
          property1: property1Actual,
          property2: property2Actual,
          _new: true,
          _dirty: false,
          _deleted: false
        }
      }

      const resultados = await saveBatch([entrada])

      if (resultados[0]?.success === false) {
        // El backend lo rechazó (ej. período bloqueado) - no es un problema
        // de red/sesión, pero igual no hay que perder el tiempo: se podría
        // reintentar luego de corregir lo que corresponda. Se deja corriendo
        // de nuevo en vez de resetear.
        reanudarTrasFalloDeGuardado(seg)
        return {
          ok: false,
          reintentable: true,
          error: resultados[0].error || 'Error desconocido',
          tiempoCargado,
          proyectoNombre: proyectoActual.text
        }
      }

      timesStore.invalidateRange(fechaActual, fechaActual)
      try {
        await timesStore.ensureRange(fechaActual, fechaActual)
      } catch {
        // el guardado ya se resolvió arriba, un refresco de cache fallido no es falla de guardado
      }

      limpiarPersistencia()
      resetAll()

      return {
        ok: true,
        tiempoCargado,
        proyectoNombre: proyectoActual.text,
        sumado: eraReanudacion || huboMatch
      }
    } catch (err) {
      // Falló antes de tener respuesta del backend (sin red, sesión vencida -
      // ej. el logout automático a las 8hs, etc.) - NUNCA se descarta acá:
      // el timer se reanuda con los mismos segundos para poder reintentar
      // (típicamente, después de volver a loguearse).
      logger.error('Error guardando el tiempo del timer:', err)
      reanudarTrasFalloDeGuardado(seg)
      return {
        ok: false,
        reintentable: true,
        error: err?.message || 'Error desconocido',
        tiempoCargado,
        proyectoNombre: proyectoActual?.text
      }
    }
  }

  /** Reanudar una fila ya guardada (botón ▶ en EntryRow). */
  function resumeTask({ fecha: f, proyecto: p, tareaId: tId, tareaNombre: tN, desc: d, property1: p1, property2: p2 }) {
    if (isRunning.value) return
    start({ fecha: f, proyecto: p, tareaId: tId, tareaNombre: tN, desc: d, property1: p1, property2: p2, reanudando: true })
  }

  /**
   * Recuperación de sesión colgada (navegador cerrado de verdad) elegida como
   * "cargar directo" en vez de "seguir corriendo" - siempre agrega una fila
   * NUEVA (nunca intenta mergear), igual que antes: es la opción más segura
   * para no mezclar mal con datos que ya pudieron haber cambiado en el medio.
   */
  async function guardarTiempoDirecto(datos, segundos) {
    const tiempo = secondsToRoundedTime(segundos)
    const timesStore = useTimesStore()
    const { saveBatch } = useTimeEntries()

    const entrada = {
      ts_Id: null,
      ts_ProjectId: datos.projectId,
      ts_Tipo: datos.tipo,
      ts_Fecha: datos.fecha,
      tareaId: datos.tareaId,
      tareadesc: datos.desc,
      tiempo,
      property1: Number(datos.property1) || 0,
      property2: Number(datos.property2) || 0,
      _new: true,
      _dirty: false,
      _deleted: false
    }

    try {
      const resultados = await saveBatch([entrada])
      timesStore.invalidateRange(datos.fecha, datos.fecha)
      await timesStore.ensureRange(datos.fecha, datos.fecha).catch(() => {})
      if (resultados[0]?.success === false) {
        return { ok: false, error: resultados[0].error || 'Error desconocido', tiempo }
      }
      return { ok: true, tiempo }
    } catch (err) {
      logger.error('Error guardando el tiempo recuperado:', err)
      return { ok: false, error: err?.message || 'Error desconocido', tiempo }
    }
  }

  function recuperarDeStorage() {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    let datos
    try {
      datos = JSON.parse(raw)
    } catch {
      limpiarPersistencia()
      return null
    }
    limpiarPersistencia()
    const segundos = Math.floor((Date.now() - datos.startedAtWallClock) / 1000)
    if (segundos < 30) return null
    return { datos, segundos }
  }

  /** Reanudar exactamente donde iba (recuperación de sesión colgada), con segundos ya acumulados. */
  function reanudarConSegundos(p, datos, segundos) {
    fecha.value = datos.fecha
    proyecto.value = p
    tareaId.value = datos.tareaId
    tareaNombre.value = datos.tareaName
    desc.value = datos.desc
    property1.value = Number(datos.property1) || 0
    property2.value = Number(datos.property2) || 0
    reanudando.value = false
    baseSeconds = segundos
    elapsedSeconds.value = segundos
    startedAt = Date.now()
    isRunning.value = true
    iniciarIntervalo()
    guardarPersistencia()
  }

  // Ventana flotante (Document Picture-in-Picture, Chrome/Edge 116+) - vive
  // acá (store global) en vez de en un componente de ruta para que
  // CUALQUIER pantalla pueda ofrecer "mover a ventana" (ver diálogo de
  // "cronómetro corriendo" en CargaView.vue) sin depender de qué componente
  // esté montado. Comparte el mismo contexto de JS que la página (no es un
  // popup ni un iframe), por eso se puede teletransportar contenido de Vue
  // adentro tal cual (ver <Teleport :to="pipTarget"> en GlobalTimerWidget.vue).
  let pipWin = null
  const pipTarget = ref(null)
  const pipActivo = computed(() => !!pipTarget.value)

  function copiarEstilosAPip(win) {
    ;[...document.styleSheets].forEach((hoja) => {
      try {
        const reglas = [...hoja.cssRules].map((r) => r.cssText).join('')
        const style = document.createElement('style')
        style.textContent = reglas
        win.document.head.appendChild(style)
      } catch {
        if (hoja.href) {
          const link = document.createElement('link')
          link.rel = 'stylesheet'
          link.href = hoja.href
          win.document.head.appendChild(link)
        }
      }
    })
  }

  async function togglePip() {
    if (pipWin) {
      pipWin.close()
      return { ok: true }
    }
    if (!('documentPictureInPicture' in window)) {
      return { ok: false, error: 'La ventana flotante solo funciona en Chrome o Edge actualizados.' }
    }
    try {
      pipWin = await window.documentPictureInPicture.requestWindow({ width: 240, height: 80 })
    } catch (err) {
      logger.warn('No se pudo abrir la ventana flotante:', err)
      return { ok: false, error: 'No se pudo abrir la ventana flotante.' }
    }
    copiarEstilosAPip(pipWin)
    pipWin.document.body.style.margin = '0'
    pipWin.document.body.style.background = '#1f2937'
    pipTarget.value = pipWin.document.body

    pipWin.addEventListener('pagehide', () => {
      pipWin = null
      pipTarget.value = null
    }, { once: true })
    return { ok: true }
  }

  function cerrarPip() {
    if (pipWin) pipWin.close()
  }

  return {
    isRunning,
    elapsedSeconds,
    fecha,
    proyecto,
    tareaId,
    tareaNombre,
    desc,
    property1,
    property2,
    reanudando,
    start,
    stopAndSave,
    discard,
    descontar,
    resumeTask,
    recuperarDeStorage,
    reanudarConSegundos,
    guardarTiempoDirecto,
    guardarPersistencia,
    pipTarget,
    pipActivo,
    togglePip,
    cerrarPip
  }
})
